/**
 * useClockTime — heure locale actualisée à chaque nouvelle minute.
 *
 * Retourne un objet { hours, minutes, display } stable entre les re-renders.
 * Le timer est synchronisé sur le changement de minute réel (pas un setInterval
 * naïf de 60 000ms qui dérive) : on attend la fin de la minute courante, puis
 * on lance un intervalle de 60 000ms.
 *
 * Pur et testable : aucune dépendance extérieure sauf Date.
 */
import { useState, useEffect } from "react";

export type ClockTime = {
  hours: number;
  minutes: number;
  /** Chaîne formatée "HH:MM" */
  display: string;
};

function getClockTime(): ClockTime {
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes();
  return {
    hours: h,
    minutes: m,
    display: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
  };
}

export function useClockTime(): ClockTime {
  const [time, setTime] = useState<ClockTime>(getClockTime);

  useEffect(() => {
    // Attendre la fin de la minute courante avant de lancer l'intervalle.
    // Ex : si on monte à 14:32:47, on attend 13 secondes, puis tick toutes les 60s.
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    let interval: ReturnType<typeof setInterval> | null = null;

    const initialTimeout = setTimeout(() => {
      setTime(getClockTime());
      interval = setInterval(() => setTime(getClockTime()), 60_000);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(initialTimeout);
      if (interval) clearInterval(interval);
    };
  }, []);

  return time;
}
